import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Chip,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Clock, MessageSquareText, ShieldCheck, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';
import { useMaintenance } from '../context/MaintenanceContext';
import { toast } from 'react-toastify';

function normalizeRoleName(value) {
  return String(value || '').toLowerCase();
}

export default function AdminSettings() {
  const { enabled, message, estimatedEnd, allowedRoles, loading, update } = useMaintenance();
  const [saving, setSaving] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [roles, setRoles] = useState([]);

  const [localEnabled, setLocalEnabled] = useState(false);
  const [localMessage, setLocalMessage] = useState('');
  const [localEstimatedEnd, setLocalEstimatedEnd] = useState('');
  const [localAllowedRoles, setLocalAllowedRoles] = useState(['admin']);

  useEffect(() => {
    setLocalEnabled(Boolean(enabled));
    setLocalMessage(String(message || ''));
    setLocalEstimatedEnd(String(estimatedEnd || ''));
    const nextRoles = Array.isArray(allowedRoles) ? allowedRoles : ['admin'];
    const normalized = nextRoles.map(normalizeRoleName).filter(Boolean);
    setLocalAllowedRoles(Array.from(new Set(['admin', ...normalized])));
  }, [allowedRoles, enabled, estimatedEnd, message]);

  useEffect(() => {
    const loadRoles = async () => {
      setRolesLoading(true);
      try {
        const res = await api.get('/roles');
        const raw = Array.isArray(res.data) ? res.data : [];
        const mapped = raw
          .map((r) => normalizeRoleName(r?.name))
          .filter(Boolean);
        setRoles(Array.from(new Set(['admin', ...mapped])));
      } catch {
        setRoles(['admin']);
      } finally {
        setRolesLoading(false);
      }
    };
    loadRoles();
  }, []);

  const roleOptions = useMemo(() => {
    const merged = Array.from(new Set(['admin', ...(roles || [])]));
    return merged;
  }, [roles]);

  const onToggleRole = (role) => {
    const normalized = normalizeRoleName(role);
    if (!normalized) return;
    if (normalized === 'admin') return;
    setLocalAllowedRoles((prev) => {
      const set = new Set((prev || []).map(normalizeRoleName));
      if (set.has(normalized)) set.delete(normalized);
      else set.add(normalized);
      set.add('admin');
      return Array.from(set);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await update({
        enabled: localEnabled,
        message: localMessage,
        estimatedEnd: localEstimatedEnd,
        allowedRoles: Array.from(new Set(['admin', ...(localAllowedRoles || [])])),
      });
      toast.success('Impostazioni manutenzione salvate');
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.message;
      const detail = status ? ` (HTTP ${status})` : '';
      toast.error(`Errore salvataggio impostazioni manutenzione${detail}${msg ? `: ${msg}` : ''}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
        <Wrench size={20} />
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          Impostazioni applicazione
        </Typography>
      </Box>

      <Card
        sx={{
          p: 3,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '999px',
              bgcolor: (theme) => alpha(theme.palette.warning.main, 0.18),
              color: 'warning.dark',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Wrench size={18} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Modalità manutenzione
          </Typography>
          </Box>
          <Chip
            size="small"
            label={localEnabled ? 'ATTIVA' : 'DISATTIVA'}
            color={localEnabled ? 'warning' : 'default'}
            variant={localEnabled ? 'filled' : 'outlined'}
            sx={{ fontWeight: 800 }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Blocca l’accesso agli utenti non autorizzati mostrando una pagina di manutenzione.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Caricamento impostazioni...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={localEnabled}
                  onChange={(e) => setLocalEnabled(e.target.checked)}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {localEnabled ? 'Manutenzione attiva' : 'Manutenzione disattiva'}
                  </Typography>
                </Box>
              }
            />

            <TextField
              label="Messaggio visualizzato agli utenti"
              value={localMessage}
              onChange={(e) => setLocalMessage(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ color: 'text.secondary' }}>
                    <MessageSquareText size={18} />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              label="Fine stimata (testo libero)"
              value={localEstimatedEnd}
              onChange={(e) => setLocalEstimatedEnd(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ color: 'text.secondary' }}>
                    <Clock size={18} />
                  </InputAdornment>
                )
              }}
            />

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ color: '#2563EB', display: 'flex', alignItems: 'center' }}>
                  <ShieldCheck size={18} />
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Accesso consentito durante la manutenzione
                </Typography>
              </Box>

              {rolesLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Caricamento ruoli...
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {roleOptions.map((r) => {
                    const normalized = normalizeRoleName(r);
                    const checked = (localAllowedRoles || []).map(normalizeRoleName).includes(normalized);
                    const disabled = normalized === 'admin';
                    return (
                      <FormControlLabel
                        key={normalized}
                        control={
                          <Checkbox
                            checked={disabled ? true : checked}
                            disabled={disabled}
                            onChange={() => onToggleRole(normalized)}
                          />
                        }
                        label={normalized === 'admin' ? 'Admin (sempre consentito)' : normalized}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button disabled={saving} onClick={handleSave}>
                {saving ? 'Salvataggio...' : 'Salva impostazioni'}
              </Button>
            </Box>
          </Box>
        )}
      </Card>
    </Box>
  );
}
